/**
 * <Seo /> — gestore SEO per singola pagina.
 *
 * Aggiorna in runtime: <title>, meta description, canonical, Open Graph e Twitter Card.
 * Non introduce dipendenze (niente react-helmet). Compatibile con React 19.
 *
 * Uso:
 *   <Seo
 *     title="Menu poke bowl a Catania"
 *     description="Scopri le nostre poke firmate..."
 *     path="/menu"
 *     image="/migrated-assets/xyz.jpg"     // opzionale, assoluto o relativo
 *     type="website"                        // opzionale
 *     jsonLd={{...}}                        // opzionale (structured data)
 *   />
 */
import { useEffect } from 'react';
import { absoluteUrl, DEFAULT_SEO, SITE_URL } from '@/lib/seo';

function upsertMeta({ name, property, content }) {
  if (!content) return;
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    if (name) el.setAttribute('name', name);
    if (property) el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  if (!data) return;
  let el = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo-id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function Seo({
  title,
  description,
  path,
  image,
  type = 'website',
  keywords,
  jsonLd,
  noindex = false,
}) {
  useEffect(() => {
    const finalTitle = title
      ? `${title} — ${DEFAULT_SEO.siteName}`
      : DEFAULT_SEO.title;
    const finalDescription = description || DEFAULT_SEO.description;
    const finalImage = absoluteUrl(image || DEFAULT_SEO.image);
    const canonical = absoluteUrl(
      path || (typeof window !== 'undefined' ? window.location.pathname : '/'),
    );

    document.title = finalTitle;

    upsertMeta({ name: 'description', content: finalDescription });
    if (keywords) upsertMeta({ name: 'keywords', content: keywords });
    upsertMeta({
      name: 'robots',
      content: noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large',
    });

    // Open Graph
    upsertMeta({ property: 'og:title', content: finalTitle });
    upsertMeta({ property: 'og:description', content: finalDescription });
    upsertMeta({ property: 'og:image', content: finalImage });
    upsertMeta({ property: 'og:url', content: canonical });
    upsertMeta({ property: 'og:type', content: type });
    upsertMeta({ property: 'og:site_name', content: DEFAULT_SEO.siteName });
    upsertMeta({ property: 'og:locale', content: DEFAULT_SEO.locale });

    // Twitter Card
    upsertMeta({ name: 'twitter:card', content: DEFAULT_SEO.twitterCard });
    upsertMeta({ name: 'twitter:title', content: finalTitle });
    upsertMeta({ name: 'twitter:description', content: finalDescription });
    upsertMeta({ name: 'twitter:image', content: finalImage });

    // Canonical
    upsertCanonical(canonical);

    // JSON-LD structured data (facoltativo, per rich results Google)
    if (jsonLd) upsertJsonLd('page', jsonLd);
  }, [title, description, path, image, type, keywords, jsonLd, noindex]);

  return null;
}

export { SITE_URL };
export default Seo;
